/**
 * Operator-facing titles for cabinet alert types, shared by the Platform health page,
 * in-app notifications and operator SMS so every surface names an issue the same way.
 *
 * Naming rule: "<part> error" when the kiosk system check reached the part but found a
 * bad state; "not responding" / "not working" only when the check is purely reachability.
 * No internal names (kiosk, tower, smart lock, POS).
 */
export const ALERT_TYPE_LABELS: Record<string, string> = {
	cabinetOffline: 'Cabinet offline',
	towerOffline: 'Cabinet offline',
	failedSystemCheck: 'Hardware check failed',
	maintenanceMode: 'Locked down',
	doorOpen: 'Door left open',
	temperature: 'Temperature out of range',
	lowStock: 'Low stock',
	emptySlots: 'Out of stock',
	noSales: 'No sales',
	salesAnomaly: 'Sales lower than usual',
	paymentTerminal: 'Payment terminal error',
	cardReader: 'Card reader error',
	smartLock: 'Fridge controller error',
	vision: 'Cameras not working',
	shelves: 'Shelf sensors not working',
	router: 'Router error',
	display: 'Top display not responding',
	etagHub: 'Price tag hub not responding',
	computer: 'Computer power issue',
	kioskError: 'Cabinet software error',
	doorUnlockFailed: 'Door failed to unlock',
	routerUptimeLow: 'Router keeps restarting',
	unstableInternet: 'Unstable internet',
}

/** `failedSystemCheck` payload keys that get bespoke labels; `posApp` is a subsystem, not an alert type. */
const SYSTEM_CHECK_SUBSYSTEM = {
	PosApp: 'posApp',
	CardReader: 'cardReader',
	SmartLock: 'smartLock',
} as const

/** Gen4 checkout app running but not in the foreground (a system-check subsystem, not an alert type of its own). */
export const POS_APP_LABEL = 'Checkout app not showing'

/** Gen4 checkout app the system check could not reach at all — reachability, so "not responding". */
export const POS_APP_UNREACHABLE_LABEL = 'Checkout app not responding'

/** A latched high temperature alarm on the fridge controller locks the cabinet for food safety. */
export const TEMPERATURE_ALARM_LABEL = 'Food safety lockout'

/** The vision QA recording test runs on the cabinet computer; a failure is a recording problem, not a camera fault. */
export const VISION_QA_LABEL = 'Camera recording test failed'

export const NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY = 'non_perishable_auto_clear' as const
export const NON_PERISHABLE_AUTO_CLEAR_LABEL = 'Auto-cleared (non-perishable mode)'
export const NON_PERISHABLE_AUTO_CLEAR_PENDING_LABEL = 'Auto-clear not confirmed (non-perishable mode)'

export interface CardReaderCheck {
	message?: string
	status?: { hasPreauthWaitedTooLong?: boolean }
}

export interface SmartLockCheck {
	connected?: boolean
}

interface SystemCheckSubsystem {
	operational?: boolean
}

/** Operator wording for a card reader fault; raw terminal diagnostics are never shown as-is. */
export function cardReaderLabel(check: Record<string, unknown> | undefined): string {
	const { message = '', status } = (check ?? {}) as CardReaderCheck
	if (status?.hasPreauthWaitedTooLong || /preauth has waited too long/i.test(message)) {
		return 'Card reader stuck on a payment'
	}
	if (/timed out|ECONNREFUSED|EHOSTUNREACH|ETIMEDOUT/i.test(message)) return 'Card reader not responding'
	if (/Diagnostic: Maintenance/i.test(message)) return 'Card reader updating'
	if (/Repeated DB Message|RepeatedTransactionId/i.test(message)) return 'Card reader needs a restart'
	return ALERT_TYPE_LABELS.cardReader
}

/** Subsystems that failed a system check, from the `failedSystemCheck` alert data. */
export function failedSubsystems(data: Record<string, unknown>): string[] {
	return Object.entries(data)
		// `meta` is response-envelope data, not a subsystem (removed from new builds, still sent by older ones).
		.filter(([key, value]) => key !== 'global' && key !== 'meta' && typeof value === 'object' && value !== null)
		// Kiosk.didPassHardCheck fails shelves only when none are operational.
		.filter(([, value]) =>
			Array.isArray(value)
				? value.length > 0 && value.every((shelf) => (shelf as SystemCheckSubsystem)?.operational === false)
				: (value as SystemCheckSubsystem).operational === false
		)
		.map(([key]) => key)
}

export function smartLockLabel(check: SmartLockCheck | null | undefined): string {
	return check?.connected === false ? 'Fridge controller not responding' : ALERT_TYPE_LABELS.smartLock
}

/** What the kiosk's display check (`Display.performSystemCheck`) saw. */
export const DISPLAY_FAULT = {
	ScreenNotDetected: 'screen-not-detected',
	AppNotResponding: 'app-not-responding',
	Unknown: 'unknown',
} as const satisfies Record<string, string>
export type DisplayFault = (typeof DISPLAY_FAULT)[keyof typeof DISPLAY_FAULT]

export interface DisplayCheck {
	/** The display app's socket to the cabinet computer. */
	connected?: boolean
	/** The computer's HDMI port: null when unreadable, absent on kiosk releases before the HDMI check. */
	status?: { hdmiConnected?: boolean | null }
}

export function displayFault(check: unknown): DisplayFault {
	const { connected, status } = (check ?? {}) as DisplayCheck
	if (status?.hdmiConnected === false) return DISPLAY_FAULT.ScreenNotDetected
	if (connected === false) return DISPLAY_FAULT.AppNotResponding
	return DISPLAY_FAULT.Unknown
}

const DISPLAY_FAULT_LABELS: Record<DisplayFault, string> = {
	[DISPLAY_FAULT.ScreenNotDetected]: 'Top screen not detected',
	[DISPLAY_FAULT.AppNotResponding]: 'Top display app not responding',
	[DISPLAY_FAULT.Unknown]: ALERT_TYPE_LABELS.display,
}

export function displayLabel(check: unknown): string {
	return DISPLAY_FAULT_LABELS[displayFault(check)]
}

export interface VisionSubsystemCheck {
	message?: string
	status?: {
		heartbeat?: CameraHeartbeat | string
	}
}

/** `*CameraConnected` booleans in pre-Gen4 heartbeats, mapped to display names. */
const LEGACY_CAMERA_NAMES: Record<string, string> = {
	topRightCameraConnected: 'Top right',
	topLeftCameraConnected: 'Top left',
	bottomLeftCameraConnected: 'Bottom left',
	bottomRightCameraConnected: 'Bottom right',
}

export const LEGACY_CAMERA_FIELDS = Object.keys(LEGACY_CAMERA_NAMES)

/** Gen4 `cameraStatuses` keys, mapped to display names. */
const GEN4_CAMERA_NAMES: Record<string, string> = {
	topRight: 'Top right',
	topLeft: 'Top left',
	bottomLeft: 'Bottom left',
	bottomRight: 'Bottom right',
}

export interface CameraHeartbeat {
	topRightCameraConnected?: boolean
	topLeftCameraConnected?: boolean
	bottomLeftCameraConnected?: boolean
	bottomRightCameraConnected?: boolean
	cameraStatuses?: Record<string, { connected?: boolean; disabled?: boolean; connectedOrDisabled?: boolean }>
	isStreamProcessRunning?: boolean
	isRecordingInProgress?: boolean
}

/**
 * Camera positions the heartbeat reports as down. Gen4's `cameraStatuses` wins when
 * present; disabled cameras are ignored. Empty when nothing was checked or all are up.
 */
export function disconnectedCameraNames(check: unknown): string[] {
	const heartbeat = (check as VisionSubsystemCheck | undefined)?.status?.heartbeat
	if (!heartbeat || typeof heartbeat !== 'object') return []
	const statuses = heartbeat.cameraStatuses
	if (statuses && typeof statuses === 'object') {
		return Object.entries(statuses).flatMap(([field, status]) => {
			if (!status || status.disabled === true || typeof status.connected !== 'boolean') return []
			return (status.connectedOrDisabled ?? status.connected) === true ? [] : [GEN4_CAMERA_NAMES[field] ?? field]
		})
	}
	return Object.entries(LEGACY_CAMERA_NAMES).flatMap(([field, name]) =>
		heartbeat[field] === false ? [name] : []
	)
}

/** Keys on `failedSystemCheck` data that are check/alert envelope, not subsystems. */
export const SYSTEM_CHECK_ENVELOPE_KEYS: readonly string[] = [
	'global',
	'meta',
	'latest_errors',
	'latest_failed_at',
	'pos_restart',
	'scheduled_restart',
	NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY,
]

/** `global.errors` entries the kiosk writes for the vision subsystem (`Vision …` / `Sandstar …`). */
const VISION_ERROR_PATTERN = /^(Vision|Sandstar)\b/
/** Per-camera lines in that error, e.g. `Top Left Camera: Error` (CoolerVision.performSystemCheck). */
const TOP_CAMERA_OK_PATTERN = /\bTop (Left|Right) Camera: Ok\b/
const CAMERA_ERROR_PATTERN = /\bCamera: Error\b/
const QA_PATTERN = /\bQA:/i

/** A subsystem value the kiosk explicitly reported as working. Missing or ambiguous shapes are not. */
function subsystemReportedOperational(value: unknown): boolean {
	if (Array.isArray(value)) {
		// Kiosk.didPassHardCheck needs at least one operational shelf.
		return value.length > 0 &&
			value.every((shelf) => typeof (shelf as SystemCheckSubsystem)?.operational === 'boolean') &&
			value.some((shelf) => (shelf as SystemCheckSubsystem).operational === true)
	}
	return !!value && typeof value === 'object' && (value as SystemCheckSubsystem).operational === true
}

/**
 * Every error is the vision check reporting a camera down while a top camera is still "Ok".
 * Checked on `latest_errors` too: retriggers keep the first check's `data` and only refresh
 * `latest_errors`, so this is what keeps the verdict tied to the latest check.
 */
function onlyPartialCameraErrors(errors: unknown): boolean {
	return Array.isArray(errors) && errors.every((error) =>
		typeof error === 'string' &&
		VISION_ERROR_PATTERN.test(error) &&
		TOP_CAMERA_OK_PATTERN.test(error) &&
		CAMERA_ERROR_PATTERN.test(error) &&
		!QA_PATTERN.test(error)
	)
}

function enabledCameras(heartbeat: CameraHeartbeat): { name: string; connected: boolean }[] {
	const statuses = heartbeat.cameraStatuses
	if (statuses && typeof statuses === 'object') {
		return Object.entries(statuses).flatMap(([name, status]) =>
			!status || status.disabled === true || typeof status.connected !== 'boolean'
				? []
				: [{ name, connected: (status.connectedOrDisabled ?? status.connected) === true }]
		)
	}
	return LEGACY_CAMERA_FIELDS.flatMap((field) => {
		const connected = heartbeat[field as keyof CameraHeartbeat]
		return typeof connected === 'boolean' ? [{ name: field.replace('CameraConnected', ''), connected }] : []
	})
}

/**
 * A `failedSystemCheck` that failed only because some cameras are down while a top camera
 * still records — the cabinet can still transact, so it surfaces as a warning rather than
 * an outage (QA-310). Anything that could mean another fault stays blocking:
 * - every other subsystem present must report `operational: true` (missing/odd shapes count as failed),
 * - `global.errors` (and `latest_errors` from later checks) may only hold vision errors that
 *   list a camera down and a top camera "Ok",
 * - the vision QA recording test must not have failed,
 * - the heartbeat must name at least one enabled camera down and one enabled top camera up
 *   (the vision service fails without a top camera), and
 * - the vision stream must not be reported down — with cameras up that is the real fault.
 */
export function isPartialCameraFailure(data: unknown): boolean {
	if (!data || typeof data !== 'object' || Array.isArray(data)) return false
	const record = data as Record<string, unknown>
	const vision = record.vision as (VisionSubsystemCheck & SystemCheckSubsystem) | undefined
	if (!vision || typeof vision !== 'object' || vision.operational !== false) return false
	const otherSubsystemsOk = Object.entries(record).every(([key, value]) =>
		key === 'vision' || SYSTEM_CHECK_ENVELOPE_KEYS.includes(key) || value === null || subsystemReportedOperational(value)
	)
	if (!otherSubsystemsOk) return false
	const global = record.global as { errors?: unknown } | undefined
	if (!global || typeof global !== 'object' || !Array.isArray(global.errors) || !global.errors.length || !onlyPartialCameraErrors(global.errors)) return false
	if (record.latest_errors !== undefined && !onlyPartialCameraErrors(record.latest_errors)) return false
	if (visionQaFailed(vision)) return false
	const heartbeat = vision.status?.heartbeat
	if (!heartbeat || typeof heartbeat !== 'object') return false
	if (heartbeat.isStreamProcessRunning === false && heartbeat.isRecordingInProgress !== true) return false
	const cameras = enabledCameras(heartbeat)
	return cameras.some((camera) => !camera.connected) &&
		cameras.some((camera) => camera.connected && camera.name.startsWith('top'))
}

/**
 * The kiosk appends a `QA: <error>` section to the vision check message only when the QA
 * recording test fails (e.g. recording speed too low); camera connectivity is reported
 * separately and can be all "Ok" when this fires.
 */
export function visionQaFailed(check: unknown): boolean {
	const message = (check as VisionSubsystemCheck | undefined)?.message ?? ''
	return QA_PATTERN.test(message)
}

export function visionLabel(check: unknown): string {
	if (visionQaFailed(check)) return VISION_QA_LABEL
	const down = disconnectedCameraNames(check)
	if (down.length === 1) return `${down[0]} camera not working`
	if (down.length > 1) return `${down.length} cameras not working`
	return ALERT_TYPE_LABELS.vision
}

/**
 * A `failedSystemCheck` whose only failure is the checkout app out of the
 * foreground (`isInForeground === false`): the terminal still answers the API —
 * usually sitting on the preauthorization page — so it warns instead of taking
 * the cabinet out of service (QA-312). `isInForeground === null` means the
 * probe could not reach the app at all, which stays blocking.
 */
export function isCheckoutAppBackgroundedOnly(data: Record<string, unknown> | null | undefined): boolean {
	if (!data) return false
	const failed = failedSubsystems(data)
	if (failed.length !== 1 || failed[0] !== SYSTEM_CHECK_SUBSYSTEM.PosApp) return false
	const posApp = data[SYSTEM_CHECK_SUBSYSTEM.PosApp] as
		| { status?: { isInForeground?: boolean | null } }
		| undefined
	return posApp?.status?.isInForeground === false
}

export function isCheckoutAppBackgroundedAlert(alert: { type?: unknown; data?: unknown }): boolean {
	return alert?.type === 'failedSystemCheck' && isCheckoutAppBackgroundedOnly(alert.data as Record<string, unknown> | null | undefined)
}

export function isPartialCameraFailureAlert(alert: { type?: unknown; data?: unknown }): boolean {
	return alert?.type === 'failedSystemCheck' && isPartialCameraFailure(alert.data)
}

export function labelledFailedParts(data: Record<string, unknown>): string[] {
	return failedSubsystems(data).filter((key) => ALERT_TYPE_LABELS[key] || key === SYSTEM_CHECK_SUBSYSTEM.PosApp)
}

export function singleFailedPartLabel(data: Record<string, unknown>): string | undefined {
	const failed = labelledFailedParts(data)
	if (failed.length !== 1) return undefined
	if (failed[0] === SYSTEM_CHECK_SUBSYSTEM.PosApp) {
		return isCheckoutAppBackgroundedOnly(data) ? POS_APP_LABEL : POS_APP_UNREACHABLE_LABEL
	}
	if (failed[0] === SYSTEM_CHECK_SUBSYSTEM.CardReader) {
		return cardReaderLabel(data.cardReader as Record<string, unknown>)
	}
	if (failed[0] === SYSTEM_CHECK_SUBSYSTEM.SmartLock) {
		return smartLockLabel(data.smartLock as SmartLockCheck)
	}
	if (failed[0] === 'vision') {
		return visionLabel(data.vision as Record<string, unknown>)
	}
	if (failed[0] === 'display') return displayLabel(data.display)
	return ALERT_TYPE_LABELS[failed[0]]
}

export function alertTypeLabel(type: string): string {
	if (ALERT_TYPE_LABELS[type]) return ALERT_TYPE_LABELS[type]
	// eslint-disable-next-line prefer-named-capture-group -- named groups need an ES2018 target
	const spaced = type.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
	return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}
